import { FC, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Link as LinkBase,
  Stack,
  Typography,
} from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { Link } from "react-router-dom";
import CustomTable from "components/Table/CustomTable";
import { Modal } from "components/Modal/Modal";
import { FeedbackStatusChip } from "components/Feedback";
import { UserWithPet } from "components/VetStore/CoachPetAvatar";
import {
  LearnerRequirementProgress,
  ProcessedLearner,
} from "../learnersAnalytics";
import { FeedbackType } from "types/feedback";
import { DomainColorSquares } from "components/Requirements";

type Props = {
  learner: ProcessedLearner;
};

const scoreLabel = (value: number | null | undefined) =>
  typeof value === "number" ? String(value) : "—";

const RequirementStatusChip: FC<{ row: LearnerRequirementProgress }> = ({
  row,
}) => {
  if (row.meetsAttempts && row.meetsScore) {
    return <Chip label="On track" color="success" size="small" />;
  }
  if (row.attempts === 0) {
    return <Chip label="Not started" color="default" size="small" />;
  }
  return <Chip label="Behind" color="warning" size="small" />;
};

export const LearnersDetail: FC<Props> = ({ learner }) => {
  const [selectedRequirement, setSelectedRequirement] =
    useState<LearnerRequirementProgress | null>(null);

  const requirementEntities = useMemo(
    () => learner.requirements,
    [learner.requirements],
  );

  if (!learner) return null;

  return (
    <Box>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <UserWithPet
            pictureUrl={learner.student.picture_url}
            name={learner.student.name}
            equippedStoreItems={learner.student.equipped_store_items}
            avatarSize={80}
          />
          <Box>
            <Typography variant="h6">{learner.student.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {learner.student.email}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>On track:</strong> {learner.onTrackCount} ·{" "}
              <strong>Behind:</strong> {learner.behindCount} ·{" "}
              <strong>Feedbacks:</strong> {learner.totalFeedbacks}
            </Typography>
          </Box>
        </Stack>

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Requirements vs feedback
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Minimum Attempts and Target Score On Rubric compared with completed
            feedback attempts and the highest coach rating.
          </Typography>
          <Box sx={{ maxHeight: "40vh", overflow: "auto" }} pr={1}>
            <CustomTable
              entities={requirementEntities}
              fieldToFilter={(row, text) =>
                row.requirement.name.toLowerCase().includes(text.toLowerCase())
              }
              columns={[
                {
                  id: "requirement",
                  label: "Requirement",
                  value: (row: LearnerRequirementProgress) => (
                    <Box>
                      <Typography variant="body2">
                        {row.requirement.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.requirement.type}
                      </Typography>
                      <Box mt={0.5}>
                        <DomainColorSquares requirement={row.requirement} />
                      </Box>
                    </Box>
                  ),
                },
                {
                  id: "attempts",
                  label: "Attempts",
                  value: (row: LearnerRequirementProgress) =>
                    `${row.attempts} / ${row.minAttempts}`,
                },
                {
                  id: "score",
                  label: "Rubric score",
                  value: (row: LearnerRequirementProgress) =>
                    `${scoreLabel(row.currentScore)} / ${scoreLabel(row.targetScore)}`,
                },
                {
                  id: "status",
                  label: "Status",
                  value: (row: LearnerRequirementProgress) => (
                    <RequirementStatusChip row={row} />
                  ),
                },
                {
                  id: "details",
                  value: (row: LearnerRequirementProgress) => (
                    <Button
                      color="primary"
                      variant="outlined"
                      size="small"
                      onClick={() => setSelectedRequirement(row)}
                    >
                      Details
                    </Button>
                  ),
                },
              ]}
              isSelectable={false}
              idColumn="requirementId"
              handleActionClick={() => {}}
              extraActionItems={[]}
              enableCardViewInSmallDevices
              enableMenuInSmallDevices={false}
            />
          </Box>
        </Box>
      </Stack>

      <Modal
        open={Boolean(selectedRequirement)}
        onClose={() => setSelectedRequirement(null)}
        title={selectedRequirement?.requirement.name || "Requirement"}
        size="large"
      >
        {selectedRequirement && (
          <RequirementFeedbackDetail progress={selectedRequirement} />
        )}
      </Modal>
    </Box>
  );
};

const RequirementFeedbackDetail: FC<{
  progress: LearnerRequirementProgress;
}> = ({ progress }) => {
  const { requirement, feedbacks } = progress;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Requirement details
        </Typography>
        <Typography variant="body2">
          <strong>Type:</strong> {requirement.type}
        </Typography>
        <Typography variant="body2">
          <strong>Minimum Attempts:</strong> {progress.minAttempts}
        </Typography>
        <Typography variant="body2">
          <strong>Target Score On Rubric:</strong>{" "}
          {scoreLabel(progress.targetScore)}
        </Typography>
        <Typography variant="body2">
          <strong>Completed attempts:</strong> {progress.attempts}
        </Typography>
        <Typography variant="body2">
          <strong>Current coach rating:</strong>{" "}
          {scoreLabel(progress.currentScore)}
        </Typography>
        {requirement.description && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {requirement.description}
          </Typography>
        )}
        {requirement.overview && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {requirement.overview}
          </Typography>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Feedback requests
        </Typography>
        {feedbacks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No feedback requests for this requirement yet.
          </Typography>
        ) : (
          <Box sx={{ maxHeight: "45vh", overflow: "auto" }} pr={1}>
            <CustomTable
              entities={feedbacks}
              fieldToFilter={(row, text) =>
                (row.coach?.name || "")
                  .toLowerCase()
                  .includes(text.toLowerCase())
              }
              columns={[
                {
                  id: "coach",
                  label: "Coach",
                  value: (row: FeedbackType) => row.coach?.name || "—",
                },
                {
                  id: "status",
                  label: "Status",
                  value: (row: FeedbackType) => (
                    <FeedbackStatusChip feedback={row} />
                  ),
                },
                {
                  id: "observation",
                  label: "Observation",
                  value: (row: FeedbackType) =>
                    row.observation_date
                      ? String(row.observation_date).slice(0, 10)
                      : "—",
                },
                {
                  id: "ratings",
                  label: "Ratings",
                  value: (row: FeedbackType) => {
                    const skill = row.feedback_skills?.find(
                      (entry) =>
                        entry.requirement?.requirement_id ===
                        progress.requirementId,
                    );
                    return `Self ${scoreLabel(skill?.self_rating)} · Coach ${scoreLabel(skill?.coach_rating)}`;
                  },
                },
                {
                  id: "open",
                  value: (row: FeedbackType) => (
                    <LinkBase
                      component={Link}
                      to={`/feedback/${row.feedback_id}`}
                      target="_blank"
                    >
                      <OpenInNew fontSize="small" />
                    </LinkBase>
                  ),
                },
              ]}
              isSelectable={false}
              idColumn="feedback_id"
              handleActionClick={() => {}}
              extraActionItems={[]}
              enableCardViewInSmallDevices
              enableMenuInSmallDevices={false}
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
};
